import type { TypeTask } from '@/types/work/types.work.task';
import { isLate } from './board.signals';
import { ancestors, descendants, isLeaf } from './task.tree';
import type { TypeWbsIssue } from './wbs';

export const blockersOf = (tasks: TypeTask[], task: TypeTask): TypeTask[] =>
    task.blockedBy.map((id) => tasks.find((entry) => entry.id === id)).filter((entry): entry is TypeTask => Boolean(entry));

export const dependentsOf = (tasks: TypeTask[], taskId: string): TypeTask[] =>
    tasks.filter((entry) => entry.blockedBy.includes(taskId));

export const openBlockers = (tasks: TypeTask[], task: TypeTask): TypeTask[] =>
    blockersOf(tasks, task).filter((entry) => entry.state !== 'done');

export const isBlocked = (tasks: TypeTask[], task: TypeTask): boolean =>
    task.state !== 'done' && openBlockers(tasks, task).length > 0;

function reaches(tasks: TypeTask[], fromId: string, targetId: string, seen = new Set<string>()): boolean {
    if (fromId === targetId) return true;
    if (seen.has(fromId)) return false;
    seen.add(fromId);
    const from = tasks.find((entry) => entry.id === fromId);
    if (!from) return false;
    return from.blockedBy.some((id) => reaches(tasks, id, targetId, seen));
}

export function canDependOn(tasks: TypeTask[], taskId: string, blockerId: string): boolean {
    if (taskId === blockerId) return false;
    const task = tasks.find((entry) => entry.id === taskId);
    const blocker = tasks.find((entry) => entry.id === blockerId);
    if (!task || !blocker) return false;
    if (task.blockedBy.includes(blockerId)) return false;
    if (ancestors(tasks, task).some((entry) => entry.id === blockerId)) return false;
    if (descendants(tasks, task.id).some((entry) => entry.id === blockerId)) return false;
    return !reaches(tasks, blockerId, taskId);
}

export const candidateBlockers = (tasks: TypeTask[], task: TypeTask): TypeTask[] =>
    tasks
        .filter((entry) => entry.projectId === task.projectId && !entry.archivedAt)
        .filter((entry) => canDependOn(tasks, task.id, entry.id));

export type TypeDependencyRisk = {
    kind: 'late' | 'after' | 'doing';
    blocker: TypeTask;
};

export function dependencyRisks(tasks: TypeTask[], task: TypeTask): TypeDependencyRisk[] {
    if (task.state === 'done') return [];
    return openBlockers(tasks, task).flatMap((blocker): TypeDependencyRisk[] => {
        const risks: TypeDependencyRisk[] = [];
        if (isLate(blocker)) risks.push({ kind: 'late', blocker });
        else if (task.dueAt && blocker.dueAt && Date.parse(blocker.dueAt) > Date.parse(task.dueAt)) risks.push({ kind: 'after', blocker });
        if (task.state === 'doing' || task.state === 'review') risks.push({ kind: 'doing', blocker });
        return risks;
    });
}

export const atRisk = (tasks: TypeTask[], task: TypeTask): boolean =>
    dependencyRisks(tasks, task).some((risk) => risk.kind !== 'doing');

export function dependencyIssues(tasks: TypeTask[], task: TypeTask): TypeWbsIssue[] {
    return dependencyRisks(tasks, task).map((risk): TypeWbsIssue => {
        if (risk.kind === 'late') {
            return {
                id: 'waits-late',
                severity: 'danger',
                label: 'Aspetta un pezzo in ritardo',
                detail: `«${task.title}» non può cominciare finché «${risk.blocker.title}» non è chiuso, e quello ha già mancato la sua data: il ritardo è già qui, anche se questa riga sembra in orario.`,
            };
        }
        if (risk.kind === 'after') {
            return {
                id: 'waits-after',
                severity: 'danger',
                label: 'Aspetta un pezzo che scade dopo',
                detail: `«${task.title}» è promesso prima di «${risk.blocker.title}», da cui dipende: una delle due date è sbagliata.`,
            };
        }
        return {
            id: 'waits-doing',
            severity: 'warning',
            label: 'In corso, ma aspetta ancora',
            detail: `«${task.title}» risulta in lavorazione mentre «${risk.blocker.title}» non è chiuso: o la dipendenza non c'è più, o si sta lavorando su qualcosa che potrà cambiare.`,
        };
    });
}

export type TypeCriticalPath = {
    ids: Set<string>;
    hours: number;
    chain: TypeTask[];
};

export function criticalPath(tasks: TypeTask[], root: TypeTask): TypeCriticalPath {
    const family = [root, ...descendants(tasks, root.id)];
    const leaves = family.filter((entry) => isLeaf(tasks, entry) && entry.state !== 'done');
    const inside = new Set(leaves.map((entry) => entry.id));
    const memo = new Map<string, { hours: number; chain: TypeTask[] }>();

    const longest = (task: TypeTask, seen: Set<string>): { hours: number; chain: TypeTask[] } => {
        const cached = memo.get(task.id);
        if (cached) return cached;
        if (seen.has(task.id)) return { hours: 0, chain: [] };
        seen.add(task.id);
        const own = task.estimateHours ?? 0;
        const before = task.blockedBy
            .map((id) => leaves.find((entry) => entry.id === id))
            .filter((entry): entry is TypeTask => Boolean(entry && inside.has(entry.id)))
            .map((entry) => longest(entry, seen));
        const best = before.sort((a, b) => b.hours - a.hours)[0];
        const result = best ? { hours: own + best.hours, chain: [...best.chain, task] } : { hours: own, chain: [task] };
        memo.set(task.id, result);
        return result;
    };

    const best = leaves
        .map((leaf) => longest(leaf, new Set()))
        .sort((a, b) => b.hours - a.hours)[0];

    if (!best || best.chain.length < 2) return { ids: new Set(), hours: 0, chain: [] };
    return { ids: new Set(best.chain.map((entry) => entry.id)), hours: best.hours, chain: best.chain };
}

export function closeBlockers(tasks: TypeTask[], task: TypeTask): TypeTask[] {
    if (task.state === 'done') return [];
    const family = new Set([task.id, ...descendants(tasks, task.id).map((entry) => entry.id)]);
    const seen = new Set<string>();
    return [task, ...descendants(tasks, task.id)]
        .flatMap((entry) => openBlockers(tasks, entry))
        .filter((blocker) => !family.has(blocker.id))
        .filter((blocker) => {
            if (seen.has(blocker.id)) return false;
            seen.add(blocker.id);
            return true;
        });
}

export const canClose = (tasks: TypeTask[], task: TypeTask): boolean => closeBlockers(tasks, task).length === 0;

export const closeReason = (tasks: TypeTask[], task: TypeTask): string | null => {
    const waits = closeBlockers(tasks, task);
    if (!waits.length) return null;
    const first = `«${waits[0].title}»`;
    return waits.length === 1
        ? `Aspetta ${first}: chiudi prima quello`
        : `Aspetta ${first} e ${waits.length - 1 === 1 ? 'un altro pezzo' : `altri ${waits.length - 1} pezzi`}: chiudi prima quelli`;
};

export const freedBy = (tasks: TypeTask[], blockerId: string): TypeTask[] =>
    dependentsOf(tasks, blockerId)
        .filter((entry) => entry.state !== 'done')
        .filter((entry) => openBlockers(tasks, entry).every((blocker) => blocker.id === blockerId));
